"use client";

import Script from "next/script";
import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_GOOGLE_CLIENT_ID =
  "365480001914-d46s1nr68o72lg4q7838n3og1s4oc67l.apps.googleusercontent.com";

type GoogleCredentialResponse = {
  credential?: string;
};

type GoogleIdentityApi = {
  initialize: (options: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type: "standard";
      theme: "outline";
      size: "large";
      text: "continue_with";
      shape: "pill";
      logo_alignment: "left";
      width: number;
    },
  ) => void;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleIdentityApi;
      };
    };
  }
}

type GoogleSignInButtonProps = {
  busy: boolean;
  onCredential: (credential: string) => void | Promise<void>;
  onUnavailable: (message: string) => void;
};

export function GoogleSignInButton({
  busy,
  onCredential,
  onUnavailable,
}: GoogleSignInButtonProps) {
  const buttonHostRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef(false);
  const busyRef = useRef(busy);
  const credentialHandlerRef = useRef(onCredential);
  const unavailableHandlerRef = useRef(onUnavailable);
  const [ready, setReady] = useState(false);

  const clientId =
    process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID?.trim() ||
    DEFAULT_GOOGLE_CLIENT_ID;

  useEffect(() => {
    busyRef.current = busy;
    credentialHandlerRef.current = onCredential;
    unavailableHandlerRef.current = onUnavailable;
  }, [busy, onCredential, onUnavailable]);

  const renderGoogleButton = useCallback(() => {
    if (renderedRef.current || !buttonHostRef.current || !window.google) return;

    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        auto_select: false,
        cancel_on_tap_outside: true,
        callback: (response) => {
          if (busyRef.current) return;
          if (!response.credential) {
            unavailableHandlerRef.current(
              "Google did not return a sign-in credential. Please try again.",
            );
            return;
          }
          void credentialHandlerRef.current(response.credential);
        },
      });
      buttonHostRef.current.replaceChildren();
      const availableWidth = buttonHostRef.current.clientWidth;
      window.google.accounts.id.renderButton(buttonHostRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "pill",
        logo_alignment: "left",
        // GIS adds a 20px frame gutter around the requested button width.
        width: availableWidth > 20 ? Math.min(360, availableWidth - 20) : 360,
      });
      renderedRef.current = true;
      setReady(true);
    } catch {
      unavailableHandlerRef.current(
        "Google sign-in could not be initialized. Please use email or phone sign-in.",
      );
    }
  }, [clientId]);

  useEffect(() => {
    renderGoogleButton();
  }, [renderGoogleButton]);

  return (
    <div className={`google-sign-in${busy ? " busy" : ""}`} aria-busy={busy}>
      <Script
        id="google-identity-services"
        src="https://accounts.google.com/gsi/client"
        strategy="afterInteractive"
        onReady={renderGoogleButton}
        onError={() =>
          unavailableHandlerRef.current(
            "Google sign-in is temporarily unavailable. Please use email or phone sign-in.",
          )
        }
      />
      <div ref={buttonHostRef} className="google-sign-in-host" />
      {!ready ? <span className="google-sign-in-status">Loading Google sign-in…</span> : null}
    </div>
  );
}
